/**
 * CampaignAnalyticsPanel — 30-day rollup for a single spotlight campaign.
 * Shows totals + a lightweight sparkline of daily events + top jobs by
 * apply-click + top referrers.
 *
 * Chart is pure SVG (no chart library — matches B4 "no new deps" guideline).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, MousePointerClick, Briefcase, Share2, TrendingUp } from "lucide-react";

const EVENT_META = {
    view:         { label: "Views",           icon: Eye,              color: "#002FA7" },
    cta_click:    { label: "CTA clicks",      icon: MousePointerClick, color: "#F97316" },
    apply_click:  { label: "Apply clicks",    icon: Briefcase,        color: "#059669" },
    share_click:  { label: "Share clicks",    icon: Share2,           color: "#7C3AED" },
};

export default function CampaignAnalyticsPanel({ campaignId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);

    useEffect(() => {
        if (!campaignId) return;
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/spotlight-campaigns/${campaignId}/analytics`, { params: { days } });
                setData(data);
            } catch { setData(null); }
            finally { setLoading(false); }
        })();
    }, [campaignId, days]);

    if (loading) return <div className="text-xs text-muted-foreground p-4">Loading analytics…</div>;
    if (!data) return <div className="text-xs text-muted-foreground p-4">No data.</div>;

    const totals = data.totals || {};
    const conv = data.conversion_pct;

    return (
        <div className="space-y-4" data-testid="campaign-analytics-panel">
            {/* Window picker */}
            <div className="flex items-center gap-2 text-xs">
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Window</span>
                {[7, 14, 30, 90].map((d) => (
                    <button
                        key={d}
                        type="button"
                        onClick={() => setDays(d)}
                        className={`h-7 px-2 border font-mono uppercase tracking-widest ${
                            days === d ? "border-ikb bg-ikb/5 text-ikb" : "border-border hover:border-foreground"
                        }`}
                        data-testid={`analytics-window-${d}`}
                    >{d}d</button>
                ))}
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border" data-testid="analytics-kpis">
                {["view", "cta_click", "apply_click", "share_click"].map((k) => {
                    const meta = EVENT_META[k];
                    const Icon = meta.icon;
                    return (
                        <div key={k} className="bg-white p-3">
                            <div className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                                <Icon className="w-3 h-3" /> {meta.label}
                            </div>
                            <div className="mt-1 font-display font-black text-2xl tracking-tight" style={{ color: meta.color }}>
                                {totals[k] || 0}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Conversion */}
            {conv != null && (
                <div className="border border-border bg-white p-3 flex items-baseline gap-3">
                    <TrendingUp className="w-4 h-4 text-emerald-700" />
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Apply-click conversion</div>
                    <div className="font-display font-black text-lg tracking-tight text-emerald-700 ml-auto" data-testid="analytics-conversion">{conv}%</div>
                </div>
            )}

            {/* Sparkline */}
            <div className="border border-border bg-white p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Daily events</div>
                <Sparkline rows={data.events_by_day || []} testId="analytics-sparkline" />
            </div>

            {/* Top jobs */}
            {(data.top_jobs || []).length > 0 && (
                <div className="border border-border bg-white" data-testid="analytics-top-jobs">
                    <header className="px-3 py-2 border-b border-border">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Top jobs by apply-click</div>
                    </header>
                    <ul className="divide-y divide-border">
                        {data.top_jobs.map((j) => (
                            <li key={j.job_id} className="px-3 py-2 flex items-center gap-3 text-sm">
                                <span className="font-mono text-xs text-ikb w-8">{j.clicks}</span>
                                <span className="truncate flex-1">{j.title || j.job_id}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Top referrers */}
            {(data.top_referrers || []).length > 0 && (
                <div className="border border-border bg-white" data-testid="analytics-top-referrers">
                    <header className="px-3 py-2 border-b border-border">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Top referrers</div>
                    </header>
                    <ul className="divide-y divide-border">
                        {data.top_referrers.map((r, i) => (
                            <li key={i} className="px-3 py-2 flex items-center gap-3 text-sm">
                                <span className="font-mono text-xs text-ikb w-8">{r.count}</span>
                                <span className="truncate flex-1 text-xs">{r.referrer}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function Sparkline({ rows, testId }) {
    if (!rows.length) {
        return <div className="text-[11px] text-muted-foreground py-6 text-center">No events in this window yet.</div>;
    }
    const H = 80, W = 640, PAD = 4;
    const max = Math.max(1, ...rows.flatMap((r) => [r.view || 0, r.apply_click || 0, r.cta_click || 0]));
    const x = (i) => PAD + (i / Math.max(1, rows.length - 1)) * (W - 2 * PAD);
    const y = (v) => H - PAD - (v / max) * (H - 2 * PAD);
    const path = (key) => rows.map((r, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(r[key] || 0).toFixed(1)}`).join(" ");
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" data-testid={testId}>
            <path d={path("view")} stroke={EVENT_META.view.color} strokeWidth="1.5" fill="none" />
            <path d={path("cta_click")} stroke={EVENT_META.cta_click.color} strokeWidth="1.5" fill="none" strokeDasharray="3 2" />
            <path d={path("apply_click")} stroke={EVENT_META.apply_click.color} strokeWidth="1.5" fill="none" />
            {rows.length <= 30 && rows.map((r, i) => (
                <circle key={i} cx={x(i)} cy={y(r.view || 0)} r="1.6" fill={EVENT_META.view.color}>
                    <title>{`${r.day}: ${r.view || 0} views, ${r.apply_click || 0} applies`}</title>
                </circle>
            ))}
        </svg>
    );
}
